/**
 * Width-bound delivery for a Cloudflare Images URL.
 *
 * Every stored property image URL names the `public` variant -- the original, up to 4096px -- and every surface
 * loaded it whatever size it rendered at: the explore grid card (293px wide) was pulling 640-1495px JPEGs at 74-238KB
 * each, about 3MB per paint (production 2026-09-17). Flexible variants let the URL carry the width instead, and
 * `f=auto` lets the edge answer AVIF or WebP to a browser that takes it. Widths are the SSOT in
 * `PROPERTY_IMAGE_DELIVERY_WIDTH`; a surface names its width, never a literal.
 *
 * Only a `public` URL on the account's delivery host is rewritten. A sentinel asset, another host, or an already-sized
 * variant passes through, so a card can never break an image it did not upload.
 */

import { CLOUDFLARE_IMAGES_DELIVERY_BASE, CLOUDFLARE_IMAGES_PUBLIC_VARIANT } from '@federa/const/urls-connect';

const PUBLIC_VARIANT_SUFFIX = `/${CLOUDFLARE_IMAGES_PUBLIC_VARIANT}`;

/** The same image, delivered at most `width` pixels wide in the browser's best format; unchanged when not ours. */
export function withDeliveryWidth(url: string, width: number): string {
	if (!url.startsWith(CLOUDFLARE_IMAGES_DELIVERY_BASE) || !url.endsWith(PUBLIC_VARIANT_SUFFIX)) return url;
	return `${url.slice(0, -PUBLIC_VARIANT_SUFFIX.length)}/w=${String(width)},f=auto`;
}
